import React, { useState, useEffect } from "react";
import { FileText, Users, AlertTriangle, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/hooks/use-auth";
import { useContractPermissions } from "@/hooks/use-contract-permissions";
import { getAuthHeaders } from "@/lib/auth-utils";

const ContractStats = () => {
  const { user } = useAuth();
  const { canViewContract } = useContractPermissions();
  const [stats, setStats] = useState({
    total: 0,
    withContract: 0,
    withoutContract: 0,
    loading: true,
  });

  const fetchContractStats = async () => {
    if (!canViewContract()) return;

    try {
      setStats((prev) => ({ ...prev, loading: true }));

      // Fetch employees to calculate contract stats
      const response = await fetch("/api/v1/employees?limit=1000", {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        const employees = data.data?.employees || [];

        // For each employee, check if they have a contract
        const contractChecks = await Promise.all(
          employees.map(async (employee) => {
            try {
              const contractResponse = await fetch(
                `/api/v1/employees/${employee.id}/contract`,
                {
                  headers: getAuthHeaders(),
                }
              );

              if (contractResponse.ok) {
                const contractData = await contractResponse.json();
                return !!contractData.contract;
              }
              return false;
            } catch {
              return false;
            }
          })
        );

        const withContract = contractChecks.filter(Boolean).length;
        const withoutContract = employees.length - withContract;

        setStats({
          total: employees.length,
          withContract,
          withoutContract,
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error fetching contract stats:", error);
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchContractStats();
  }, []);

  if (!canViewContract() || stats.loading) {
    return null;
  }

  const completionRate =
    stats.total > 0 ? (stats.withContract / stats.total) * 100 : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Employés</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Avec Contrat</CardTitle>
          <CheckCircle className="h-4 w-4 text-green-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">
            {stats.withContract}
          </div>
          <Progress value={completionRate} className="mt-2 h-2" />
          <p className="text-xs text-muted-foreground mt-2">
            {completionRate.toFixed(1)}% des employés
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Sans Contrat</CardTitle>
          <AlertTriangle className="h-4 w-4 text-orange-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-orange-600">
            {stats.withoutContract}
          </div>
          {stats.withoutContract > 0 && (
            <Badge variant="outline" className="mt-2 text-xs">
              Action requise
            </Badge>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractStats;
